import React from 'react';
import { clsx } from 'clsx';

interface AvatarProps {
  emoji: string;
  name: string;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  level?: number;
  stars?: number;
  showName?: boolean;
  className?: string;
}

const sizeMap = {
  xs: { emoji: 'text-base w-7 h-7', name: 'text-xs', badge: 'text-[9px] px-1 py-px' },
  sm: { emoji: 'text-xl w-9 h-9',  name: 'text-xs', badge: 'text-[9px] px-1 py-px' },
  md: { emoji: 'text-2xl w-11 h-11', name: 'text-sm', badge: 'text-xs px-1.5 py-px' },
  lg: { emoji: 'text-3xl w-14 h-14', name: 'text-base', badge: 'text-xs px-2 py-0.5' },
  xl: { emoji: 'text-4xl w-20 h-20', name: 'text-lg',  badge: 'text-sm px-2 py-0.5' },
};

const levelColors = [
  'from-slate-400 to-slate-600',   // 1
  'from-green-400 to-green-600',   // 2
  'from-teal-400 to-teal-600',     // 3
  'from-blue-400 to-blue-600',     // 4
  'from-violet-400 to-violet-600', // 5
  'from-purple-400 to-purple-600', // 6
  'from-rose-400 to-rose-600',     // 7
  'from-orange-400 to-orange-600', // 8
  'from-amber-400 to-yellow-500',  // 9
  'from-yellow-300 to-amber-500',  // 10 (gold)
];

function getLevelGradient(level: number): string {
  return levelColors[Math.min(level - 1, levelColors.length - 1)];
}

export default function Avatar({
  emoji,
  name,
  size = 'md',
  level,
  stars,
  showName = false,
  className,
}: AvatarProps): React.ReactElement {
  const s = sizeMap[size];
  const gradient = level ? getLevelGradient(level) : 'from-brand-600 to-brand-800';

  return (
    <div className={clsx('flex flex-col items-center gap-1', className)}>
      <div className="relative">
        {/* Avatar ring (level-colored gradient) */}
        <div
          className={clsx(
            'rounded-full bg-gradient-to-br p-[2px]',
            gradient,
          )}
        >
          <div
            className={clsx(
              'flex items-center justify-center rounded-full bg-surface-card',
              s.emoji,
            )}
          >
            {emoji}
          </div>
        </div>

        {/* Level badge */}
        {level !== undefined && (
          <span
            className={clsx(
              'absolute -bottom-1 -right-1 rounded-full font-bold text-slate-900',
              `bg-gradient-to-br ${gradient}`,
              s.badge,
            )}
          >
            {level}
          </span>
        )}
      </div>

      {/* Name */}
      {showName && (
        <span className={clsx('font-medium text-slate-900 truncate max-w-full', s.name)}>
          {name}
        </span>
      )}

      {/* Stars */}
      {stars !== undefined && (
        <span className="text-yellow-400 text-xs font-semibold">
          ⭐ {stars.toLocaleString()}
        </span>
      )}
    </div>
  );
}
